import classNames from "classnames";
import { useFeature } from "flagged";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import style from "./pricing-tier-view.scss";
import { CHECKMARK_SIZE, STROKE_WIDTH, updateSelectedTierTitle } from "./PricingTierView";
import Checkmark from "components/icons/Checkmark";
import StaticTable from "components/support/api-guide/StaticTable";
import { CombinedTier, OldLicensingModelTier, TenantType } from "domain/tenants";
import { generateTenantTypeToTranslationMap, generateTierToTranslationMap } from "domain/tierRelatedMaps";
import { FLAG_AI_ADDONS, FLAG_ASSET_MANAGEMENT, FLAG_FILE_SHARING } from "services/feature/FeatureFlagService";
import { StoreState } from "store";

import testIds from "testIds.json";

interface Props {
    selectedTier: CombinedTier;
    tenantType: TenantType;
}

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});

const connector = connect(mapState);

const OldModelPricingTierView = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const isAssetManagementFlagActive = useFeature(FLAG_ASSET_MANAGEMENT);
    const isFileSharingFlagActive = useFeature(FLAG_FILE_SHARING);
    const isAIAddonsFlagActive = useFeature(FLAG_AI_ADDONS);

    const cellData = [
        {
            feature: "Blancco license view and entitlements",
            basic: true,
            standard: true,
            advanced: true,
            addOns: false,
        },
        {
            feature: "Product downloads",
            basic: true,
            standard: true,
            advanced: true,
            addOns: false,
        },
        {
            feature: "Knowledge base",
            basic: true,
            standard: true,
            advanced: true,
            addOns: false,
        },
        {
            feature: "Technical support",
            basic: true,
            standard: true,
            advanced: true,
            addOns: false,
        },
        {
            feature: "Key value store",
            basic: true,
            standard: true,
            advanced: true,
            addOns: false,
        },
        {
            feature: "License alerts",
            basic: true,
            standard: true,
            advanced: true,
            addOns: true,
        },
        {
            feature: "License consumption",
            basic: true,
            standard: true,
            advanced: true,
            addOns: false,
        },
        {
            feature: "BMP APIs",
            basic: false,
            standard: true,
            advanced: true,
            addOns: false,
        },
        {
            feature: "Overview dashboard",
            basic: false,
            standard: true,
            advanced: true,
            addOns: false,
        },
        {
            feature: "Report template library",
            basic: false,
            standard: false,
            advanced: true,
            addOns: true,
        },
        {
            feature: "Reporting (Custom views)",
            basic: false,
            standard: true,
            advanced: true,
            addOns: true,
        },
        {
            feature: "Sustainability dashboard",
            basic: false,
            standard: false,
            advanced: true,
            addOns: true,
        },
        {
            feature: "Intelligent business routing (IBR / Workflows)",
            basic: false,
            standard: false,
            advanced: false,
            addOns: true,
        },
        {
            feature: "Single Sign-On (SSO)",
            basic: false,
            standard: false,
            advanced: false,
            addOns: true,
        },
        {
            feature: "Tenant management",
            basic: false,
            standard: false,
            advanced: false,
            addOns: true,
        },
        {
            feature: "License pools",
            basic: false,
            standard: false,
            advanced: true,
            addOns: true,
        },
        {
            feature: "User groups",
            basic: false,
            standard: true,
            advanced: true,
            addOns: false,
        },
    ];

    isAssetManagementFlagActive &&
        cellData.push({
            feature: "Circulate",
            basic: false,
            standard: false,
            advanced: false,
            addOns: true,
        });
    isFileSharingFlagActive &&
        cellData.push({
            feature: "File sharing",
            basic: false,
            standard: false,
            advanced: true,
            addOns: true,
        });
    isAIAddonsFlagActive &&
        cellData.push(
            {
                feature: "MCP Server",
                basic: false,
                standard: false,
                advanced: false,
                addOns: true,
            },
            {
                feature: "AI agent",
                basic: false,
                standard: false,
                advanced: false,
                addOns: true,
            }
        );

    return (
        <div>
            <div className={style.summaryOldModel}>
                <Trans
                    i18nKey={"AddCustomerForm.pricingTierDialog.summary1"}
                    values={{
                        tenantType: generateTenantTypeToTranslationMap(t).get(props.tenantType),
                        tierPlan: generateTierToTranslationMap(t).get(props.selectedTier),
                        tierOrBundle: t("AddCustomerForm.pricingTierDialog.tier"),
                    }}
                    components={[<strong key={"tenantType"} />]}
                />
            </div>
            <div className={style.tableContainer}>
                <StaticTable
                    headers={[
                        {
                            className: style.firstColumnHeader,
                            value: t("AddCustomerForm.pricingTierDialog.header.features"),
                        },
                        {
                            className: style.nthColumnHeader,
                            value: (
                                <div className={style.headerContaner}>
                                    {updateSelectedTierTitle(
                                        props.selectedTier === OldLicensingModelTier.BASIC,
                                        OldLicensingModelTier.BASIC,
                                        OldLicensingModelTier.BASIC,
                                        t
                                    )}
                                </div>
                            ),
                        },
                        {
                            className: style.nthColumnHeader,
                            value: (
                                <div className={style.headerContaner}>
                                    {updateSelectedTierTitle(
                                        props.selectedTier === OldLicensingModelTier.STANDARD,
                                        OldLicensingModelTier.STANDARD,
                                        OldLicensingModelTier.STANDARD,
                                        t
                                    )}
                                </div>
                            ),
                        },
                        {
                            className: style.nthColumnHeader,
                            value: (
                                <div className={style.headerContaner}>
                                    {updateSelectedTierTitle(
                                        props.selectedTier === OldLicensingModelTier.ADVANCED,
                                        OldLicensingModelTier.ADVANCED,
                                        OldLicensingModelTier.ADVANCED,
                                        t
                                    )}
                                </div>
                            ),
                        },
                        {
                            className: style.nthColumnHeader,
                            value: (
                                <div className={style.headerContaner}>
                                    {t("AddCustomerForm.pricingTierDialog.header.addOns")}
                                </div>
                            ),
                        },
                    ]}
                    styledCells={cellData.map((data, key) => {
                        return [
                            <td key={"feature" + key}>
                                <div>{data.feature}</div>
                            </td>,
                            <td
                                key={"basic" + key}
                                className={classNames({
                                    [style.tierFirstLevelColor]: props.selectedTier === OldLicensingModelTier.BASIC,
                                })}
                            >
                                <div className={style.checkMarkCenter}>
                                    {data.basic ? (
                                        <Checkmark
                                            color={style.homeCurrentPlanIconYellowColor}
                                            size={CHECKMARK_SIZE}
                                            strokeWidth={STROKE_WIDTH}
                                        />
                                    ) : null}
                                </div>
                            </td>,
                            <td
                                key={"standard" + key}
                                className={classNames({
                                    [style.tierSecondLevelColor]: props.selectedTier === OldLicensingModelTier.STANDARD,
                                })}
                            >
                                <div className={style.checkMarkCenter}>
                                    {data.standard ? (
                                        <Checkmark
                                            color={props.theme.homeCurrentPlanIconBlueColor}
                                            size={CHECKMARK_SIZE}
                                            strokeWidth={STROKE_WIDTH}
                                        />
                                    ) : null}
                                </div>
                            </td>,
                            <td
                                key={"advanced" + key}
                                className={classNames({
                                    [style.tierThirdLevelColor]: props.selectedTier === OldLicensingModelTier.ADVANCED,
                                })}
                            >
                                <div key={"advanced" + key} className={style.checkMarkCenter}>
                                    {data.advanced ? (
                                        <Checkmark
                                            color={props.theme.homeCurrentPlanIconRedColor}
                                            size={CHECKMARK_SIZE}
                                            strokeWidth={STROKE_WIDTH}
                                        />
                                    ) : null}
                                </div>
                            </td>,
                            <td key={"addOns" + key}>
                                <div className={style.checkMarkCenter}>
                                    {data.addOns ? (
                                        <Checkmark
                                            color={props.theme.addOnsCheckmarkColor}
                                            size={CHECKMARK_SIZE}
                                            strokeWidth={STROKE_WIDTH}
                                        />
                                    ) : null}
                                </div>
                            </td>,
                        ];
                    })}
                    tableClass={style.table}
                    testId={testIds.workArea.tenant.pricingTierDialog.table.itself}
                />
            </div>
        </div>
    );
};
export default connector(OldModelPricingTierView);
